<template>
    <div class="admin-places-page">
        <div class="page-header">
            <h2>Places</h2>

            <div class="add-place-box">
                <input
                    v-model="searchQuery"
                    @input="searchPlaces"
                    placeholder="Search place by name or address..."
                    class="form-control"
                />

                <ul v-if="suggestions.length" class="autocomplete-list">
                    <li
                        v-for="s in suggestions"
                        :key="s.id"
                        @click="selectPlace(s)"
                    >
                        <span>{{ s.label }}</span>
                    </li>
                </ul>

                <select v-model="newPlace.category" class="form-control">
                    <option disabled value="">Category</option>
                    <option v-for="c in categories" :key="c" :value="c">
                        {{ c }}
                    </option>
                </select>

                <div class="row">
                    <div class="col-md-6">
                        <button class="action-btn save" @click="createPlace">
                            {{ creatingPlace ? 'Adding...' : 'Add' }}
                        </button>
                    </div>
                    <div class="col-md-6">
                        <button class="action-btn save" @click="savePlaceToLibrary">
                            {{ savingPlace ? 'Saving...' : 'Save' }}
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="selectedSuggestionLabel" class="selected-place-preview">
                Selected: <strong>{{ selectedSuggestionLabel }}</strong>
            </div>
        </div>

        <div v-if="loading" class="state-box">
            Зареждане на places...
        </div>

        <div v-else-if="errorMessage" class="state-box error">
            {{ errorMessage }}
        </div>

        <div v-else>
            <!-- SAVED PLACES -->
            <div class="places-card">
                <div class="places-card-header">
                    <h4>Saved Places</h4>
                    <span class="places-count">{{ savedPlaces.length }} items</span>
                </div>

                <div v-if="!savedPlaces.length" class="state-box">
                    Няма записани places.
                </div>

                <div v-else class="table-wrap">
                    <table class="places-table">
                        <thead>
                        <tr>
                            <th>Photo</th>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Address</th>
                            <th>Type</th>
                            <th>Rating</th>
                            <th>Saved</th>
                            <th>Actions</th>
                        </tr>
                        </thead>

                        <tbody>
                        <tr v-for="place in savedPlaces" :key="place.id">
                            <td>
                                <div class="photo-cell">
                                    <img
                                        v-if="place.photoUrl"
                                        :src="place.photoUrl"
                                        class="place-photo"
                                        @error="onImageError"
                                    />
                                    <span v-else>-</span>
                                </div>
                            </td>

                            <td>{{ place.name || '-' }}</td>
                            <td>{{ place.category || '-' }}</td>
                            <td>{{ place.address || '-' }}</td>
                            <td>{{ place.typeLabel || '-' }}</td>
                            <td>{{ place.rating ?? '-' }}</td>
                            <td>{{ formatTimestamp(place.updatedAt || place.savedAt || place.createdAt) }}</td>

                            <td>
                                <div class="actions-col">
                                    <button
                                        class="action-btn save"
                                        @click="addSavedPlaceToRecommended(place)"
                                    >
                                        Add
                                    </button>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- RECOMMENDED PLACES -->
            <div class="places-card">
                <div class="places-card-header">
                    <h4>Recommended Places</h4>
                    <span class="places-count">{{ places.length }} items</span>
                </div>

                <div v-if="!places.length" class="state-box">
                    Няма намерени places.
                </div>

                <div v-else class="table-wrap">
                    <table class="places-table">
                        <thead>
                        <tr>
                            <th>Photo</th>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Address</th>
                            <th>Order</th>
                            <th>Sponsored</th>
                            <th>Open Now</th>
                            <th>Lat</th>
                            <th>Lng</th>
                            <th>Created</th>
                            <th>Expires</th>
                            <th>Actions</th>
                        </tr>
                        </thead>

                        <tbody>
                        <tr
                            v-for="(place, index) in places"
                            :key="place.id"
                            draggable="true"
                            @dragstart="onDragStart(index)"
                            @dragover.prevent
                            @drop="onDrop(index)"
                        >
                            <td>
                                <div class="photo-cell">
                                    <img
                                        v-if="place.photoUrl"
                                        :src="place.photoUrl"
                                        class="place-photo"
                                        @error="onImageError"
                                    />
                                    <span v-else>-</span>
                                </div>
                            </td>

                            <td>{{ place.name || '-' }}</td>
                            <td>{{ place.category || '-' }}</td>
                            <td>{{ place.address || '-' }}</td>

                            <td class="drag-handle">☰</td>

                            <td>
                                <label class="switch-wrap">
                                    <input
                                        type="checkbox"
                                        v-model="place.isSponsored"
                                        @change="updateSponsoredState(place)"
                                    />
                                    <span class="switch-label">
                                        {{ place.isSponsored ? 'Yes' : 'No' }}
                                    </span>
                                </label>
                            </td>

                            <td>
                                <span :class="place.openNow ? 'badge yes' : 'badge no'">
                                    {{ place.openNow ? 'Open' : 'Closed' }}
                                </span>
                            </td>

                            <td>{{ place.lat ?? '-' }}</td>
                            <td>{{ place.lng ?? '-' }}</td>
                            <td>{{ formatTimestamp(place.createdAt) }}</td>
                            <td>{{ formatTimestamp(place.expiresAt) }}</td>

                            <td>
                                <div class="actions-col">
                                    <button class="action-btn save" @click="saveRecommendedPlace(place)">
                                        Save
                                    </button>

                                    <button class="action-btn delete" @click="deletePlace(place)">
                                        Delete
                                    </button>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Bootstrap modal -->
        <div
            class="modal fade"
            id="photoUploadModal"
            tabindex="-1"
            aria-labelledby="photoUploadModalLabel"
            aria-hidden="true"
            ref="photoUploadModalEl"
        >
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content bg-dark text-white border-0">
                    <div class="modal-header border-0">
                        <h5 class="modal-title" id="photoUploadModalLabel">Upload photo</h5>
                        <button
                            type="button"
                            class="btn-close btn-close-white"
                            @click="closePhotoUploadModal"
                        ></button>
                    </div>

                    <div class="modal-body">
                        <p>
                            Това място няма снимка от Google Places. Качи ръчно снимка, за да продължим.
                        </p>

                        <input
                            type="file"
                            accept="image/*"
                            @change="onManualPhotoSelected"
                            class="form-control"
                        />

                        <div v-if="manualPhotoPreview" class="photo-upload-preview mt-3">
                            <img
                                :src="manualPhotoPreview"
                                alt="Manual upload preview"
                                class="manual-preview-image"
                            />
                        </div>
                    </div>

                    <div class="modal-footer border-0">
                        <button class="action-btn save" @click="confirmManualPhotoAction">
                            {{ pendingAction === 'save' ? (savingPlace ? 'Uploading...' : 'Upload & Save') : (creatingPlace ? 'Uploading...' : 'Upload & Add') }}
                        </button>

                        <button class="action-btn delete" @click="closePhotoUploadModal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from "vue";
import { Modal } from "bootstrap";
import {
    collection,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    writeBatch,
    addDoc,
    setDoc,
    onSnapshot
} from "firebase/firestore";
import {
    getStorage,
    ref,
    uploadBytes,
    getDownloadURL
} from "firebase/storage";
import { sentrysDb } from "@/firebase";

export default {
    name: "AdminPlaces",

    data() {
        return {
            loading: false,
            errorMessage: "",
            places: [],
            savedPlaces: [],
            dragIndex: null,

            searchQuery: "",
            suggestions: [],
            selectedSuggestion: null,
            selectedSuggestionLabel: "",
            autocompleteRequestId: 0,

            placesLibReady: false,
            AutocompleteSuggestionClass: null,
            AutocompleteSessionTokenClass: null,
            sessionToken: null,

            creatingPlace: false,
            savingPlace: false,
            pendingAction: null,

            photoUploadModalInstance: null,
            manualPhotoFile: null,
            manualPhotoPreview: "",

            newPlace: {
                name: "",
                address: "",
                lat: null,
                lng: null,
                placeId: "",
                photoUrl: "",
                category: "",
                isSponsored: false,
                openNow: false,
                rating: 0,
                userRatings: 0,
                typeLabel: "",
                photoName: "",
            },

            categories: [
                "NATIONAL",
                "PHARMACY",
                "HOSPITAL",
                "METRO",
                "MALL",
                "CHAINS",
                "CAFE",
                "SCHOOLS"
            ],
            recommendedPlacesUnsub: null,
            savedPlacesUnsub: null,
        };
    },

    async mounted() {
        this.subscribeToPlaces();
        await this.initPlacesNew();

        if (this.$refs.photoUploadModalEl) {
            this.photoUploadModalInstance = new Modal(this.$refs.photoUploadModalEl, {
                backdrop: "static",
                keyboard: false
            });
        }
    },

    beforeUnmount() {
        if (this.recommendedPlacesUnsub) {
            this.recommendedPlacesUnsub();
            this.recommendedPlacesUnsub = null;
        }

        if (this.savedPlacesUnsub) {
            this.savedPlacesUnsub();
            this.savedPlacesUnsub = null;
        }
    },

    methods: {
        async initPlacesNew() {
            try {
                if (!window.google || !window.google.maps || !window.google.maps.importLibrary) {
                    console.error("Google Maps JS API is not loaded.");
                    return;
                }

                const placesLib = await window.google.maps.importLibrary("places");
                this.AutocompleteSuggestionClass = placesLib.AutocompleteSuggestion;
                this.AutocompleteSessionTokenClass = placesLib.AutocompleteSessionToken;
                this.sessionToken = new this.AutocompleteSessionTokenClass();
                this.placesLibReady = true;
            } catch (error) {
                console.error("Places (New) init error:", error);
            }
        },

        subscribeToPlaces() {
            this.loading = true;
            this.errorMessage = "";

            let recommendedReady = false;
            let savedReady = false;

            const finishLoadingIfReady = () => {
                if (recommendedReady && savedReady) {
                    this.loading = false;
                }
            };

            this.recommendedPlacesUnsub = onSnapshot(
                collection(sentrysDb, "recommended_places"),
                (snapshot) => {
                    this.places = snapshot.docs
                        .map((d) => ({ id: d.id, ...d.data() }))
                        .sort((a, b) => (a.order ?? 999999) - (b.order ?? 999999));

                    recommendedReady = true;
                    finishLoadingIfReady();
                },
                (error) => {
                    console.error("Recommended places snapshot error:", error);
                    this.errorMessage = "Грешка при зареждане на recommended places.";
                    this.loading = false;
                }
            );

            this.savedPlacesUnsub = onSnapshot(
                collection(sentrysDb, "saved_places"),
                (snapshot) => {
                    this.savedPlaces = snapshot.docs
                        .map((d) => ({ id: d.id, ...d.data() }))
                        .sort((a, b) =>
                            this.toMillis(b.updatedAt || b.savedAt || b.createdAt) -
                            this.toMillis(a.updatedAt || a.savedAt || a.createdAt)
                        );

                    savedReady = true;
                    finishLoadingIfReady();
                },
                (error) => {
                    console.error("Saved places snapshot error:", error);
                    this.errorMessage = "Грешка при зареждане на saved places.";
                    this.loading = false;
                }
            );
        },

        async searchPlaces() {
            const query = (this.searchQuery || "").trim();

            this.selectedSuggestion = null;
            this.selectedSuggestionLabel = "";
            this.resetNewPlace();

            if (!query) {
                this.suggestions = [];
                return;
            }

            if (!this.placesLibReady || !this.AutocompleteSuggestionClass) {
                console.error("Places (New) library is not ready.");
                return;
            }

            const requestId = ++this.autocompleteRequestId;

            try {
                const request = {
                    input: query,
                    includedRegionCodes: ["bg"],
                    sessionToken: this.sessionToken,
                };

                const response =
                    await this.AutocompleteSuggestionClass.fetchAutocompleteSuggestions(request);

                if (requestId !== this.autocompleteRequestId) return;

                const rawSuggestions = response?.suggestions || [];

                this.suggestions = rawSuggestions.map((item, index) => {
                    const prediction = item?.placePrediction;
                    const textValue = prediction?.text;

                    let label = "";
                    if (typeof textValue === "string") {
                        label = textValue;
                    } else if (textValue && typeof textValue.toString === "function") {
                        label = textValue.toString();
                    }

                    return {
                        id: prediction?.placeId || `suggestion-${index}`,
                        label,
                        raw: markRaw(item),
                    };
                });
            } catch (error) {
                console.error("Places (New) autocomplete error:", error);
                this.suggestions = [];
            }
        },

        selectPlace(suggestion) {
            this.suggestions = [];
            this.searchQuery = suggestion?.label || "";
            this.selectedSuggestion = suggestion;
            this.selectedSuggestionLabel = suggestion?.label || "";
            this.resetNewPlace();
        },

        async hydrateSelectedPlaceDetails() {
            const prediction = this.selectedSuggestion?.raw?.placePrediction;
            if (!prediction) {
                throw new Error("No selected suggestion.");
            }

            const place = prediction.toPlace();

            await place.fetchFields({
                fields: [
                    "displayName",
                    "formattedAddress",
                    "location",
                    "photos",
                    "id",
                    "regularOpeningHours",
                    "rating",
                    "userRatingCount",
                    "primaryType"
                ]
            });

            const firstPhoto = place.photos?.[0];

            this.newPlace = {
                ...this.newPlace,
                name: place.displayName || "",
                address: place.formattedAddress || "",
                placeId: place.id || prediction.placeId || "",
                lat: place.location ? place.location.lat() : null,
                lng: place.location ? place.location.lng() : null,
                photoUrl: firstPhoto
                    ? firstPhoto.getURI({ maxWidth: 1200, maxHeight: 1200 })
                    : "",
                openNow: Boolean(place.regularOpeningHours?.openNow),
                rating: typeof place.rating === "number" ? place.rating : 0.0,
                userRatings: typeof place.userRatingCount === "number" ? place.userRatingCount : 0,
                typeLabel: place.primaryType || "",
                category: this.newPlace.category,
                isSponsored: !!this.newPlace.isSponsored,
            };

            if (this.AutocompleteSessionTokenClass) {
                this.sessionToken = new this.AutocompleteSessionTokenClass();
            }
        },

        async createPlace() {
            if (!this.selectedSuggestion) {
                alert("Моля избери място от autocomplete предложенията.");
                return;
            }

            if (!this.newPlace.category) {
                alert("Моля избери категория.");
                return;
            }

            this.pendingAction = "add";
            this.creatingPlace = true;

            try {
                await this.hydrateSelectedPlaceDetails();

                if (!this.newPlace.name || !this.newPlace.placeId) {
                    throw new Error("Missing place details.");
                }

                if (this.places.some((p) => p.placeId === this.newPlace.placeId)) {
                    alert("Already exists in recommended");
                    this.creatingPlace = false;
                    return;
                }

                if (!this.newPlace.photoUrl) {
                    this.creatingPlace = false;
                    this.openPhotoUploadModal();
                    return;
                }

                await this.finalizeRecommendedCreate();
            } catch (error) {
                console.error("Create place error:", error);
                alert("Грешка при взимане на данни за мястото.");
                this.creatingPlace = false;
            }
        },

        async savePlaceToLibrary() {
            if (!this.selectedSuggestion) {
                alert("Моля избери място от autocomplete предложенията.");
                return;
            }

            if (!this.newPlace.category) {
                alert("Моля избери категория.");
                return;
            }

            this.pendingAction = "save";
            this.savingPlace = true;

            try {
                await this.hydrateSelectedPlaceDetails();

                if (!this.newPlace.name || !this.newPlace.placeId) {
                    throw new Error("Missing place details.");
                }

                if (!this.newPlace.photoUrl) {
                    this.savingPlace = false;
                    this.openPhotoUploadModal();
                    return;
                }

                await this.finalizeSavedCreate();
            } catch (error) {
                console.error("Save place to library error:", error);
                alert("Грешка при запис на мястото.");
                this.savingPlace = false;
            }
        },

        async addSavedPlaceToRecommended(savedPlace) {
            if (!savedPlace || !savedPlace.placeId) return;

            if (this.places.some((p) => p.placeId === savedPlace.placeId)) {
                alert("Already exists in recommended");
                return;
            }

            try {
                const payload = this.buildRecommendedPayloadFromSaved(savedPlace);
                await addDoc(collection(sentrysDb, "recommended_places"), payload);
            } catch (error) {
                console.error("Add saved place to recommended error:", error);
                alert("Грешка при добавяне на saved place.");
            }
        },

        async confirmManualPhotoAction() {
            if (!this.manualPhotoFile) {
                alert("Моля качи снимка.");
                return;
            }

            if (this.pendingAction === "save") {
                await this.finalizeSavedCreate();
            } else {
                await this.finalizeRecommendedCreate();
            }
        },

        async finalizeRecommendedCreate() {
            this.creatingPlace = true;

            try {
                const photoMeta = await this.uploadPhotoToStorage();
                const payload = this.buildRecommendedPayload(photoMeta);

                await addDoc(collection(sentrysDb, "recommended_places"), payload);

                this.resetAfterPlaceAction();
            } catch (error) {
                console.error("Finalize recommended create error:", error);
                alert("Грешка при добавяне на мястото.");
            } finally {
                this.creatingPlace = false;
            }
        },

        async finalizeSavedCreate() {
            this.savingPlace = true;

            try {
                const photoMeta = await this.uploadPhotoToStorage();
                const payload = this.buildSavedPayload(photoMeta);

                await setDoc(
                    doc(sentrysDb, "saved_places", this.newPlace.placeId),
                    payload,
                    { merge: true }
                );

                this.resetAfterPlaceAction();
            } catch (error) {
                console.error("Finalize saved create error:", error);
                alert("Грешка при записване на мястото.");
            } finally {
                this.savingPlace = false;
            }
        },

        buildRecommendedPayload(photoMeta) {
            const nowMs = Date.now();
            const expiresMs = nowMs + 24 * 60 * 60 * 1000;
            const nowDate = new Date(nowMs);
            const expiresDate = new Date(expiresMs);

            return {
                address: this.newPlace.address || "",
                category: this.newPlace.category || "",
                createdAt: nowDate,
                expiresAt: expiresDate,
                isSponsored: !!this.newPlace.isSponsored,
                lat: this.newPlace.lat,
                lng: this.newPlace.lng,
                name: this.newPlace.name || "",
                order: this.places.length,
                photoCachedAt: nowMs,
                photoExpiresAt: expiresMs,
                photoName: photoMeta.photoName,
                photoUrl: photoMeta.photoUrl,
                placeId: this.newPlace.placeId || "",
                rating: typeof this.newPlace.rating === "number" ? this.newPlace.rating : null,
                ts: nowDate,
                typeLabel: this.newPlace.typeLabel || "",
                updatedAt: nowDate,
                userRatings: typeof this.newPlace.userRatings === "number" ? this.newPlace.userRatings : 0,
                openNow: !!this.newPlace.openNow,
            };
        },

        buildSavedPayload(photoMeta) {
            const nowMs = Date.now();
            const nowDate = new Date(nowMs);

            return {
                address: this.newPlace.address || "",
                category: this.newPlace.category || "",
                createdAt: nowDate,
                savedAt: nowDate,
                updatedAt: nowDate,
                isSponsored: !!this.newPlace.isSponsored,
                lat: this.newPlace.lat,
                lng: this.newPlace.lng,
                name: this.newPlace.name || "",
                photoCachedAt: nowMs,
                photoExpiresAt: nowMs + 24 * 60 * 60 * 1000,
                photoName: photoMeta.photoName,
                photoUrl: photoMeta.photoUrl,
                placeId: this.newPlace.placeId || "",
                rating: typeof this.newPlace.rating === "number" ? this.newPlace.rating : null,
                typeLabel: this.newPlace.typeLabel || "",
                userRatings: typeof this.newPlace.userRatings === "number" ? this.newPlace.userRatings : 0,
                openNow: !!this.newPlace.openNow,
            };
        },

        buildRecommendedPayloadFromSaved(savedPlace) {
            const nowMs = Date.now();
            const expiresMs = nowMs + 24 * 60 * 60 * 1000;
            const nowDate = new Date(nowMs);
            const expiresDate = new Date(expiresMs);

            return {
                address: savedPlace.address || "",
                category: savedPlace.category || "",
                createdAt: nowDate,
                expiresAt: expiresDate,
                isSponsored: !!savedPlace.isSponsored,
                lat: savedPlace.lat ?? null,
                lng: savedPlace.lng ?? null,
                name: savedPlace.name || "",
                order: this.places.length,
                photoCachedAt: savedPlace.photoCachedAt || nowMs,
                photoExpiresAt: savedPlace.photoExpiresAt || expiresMs,
                photoName: savedPlace.photoName || "",
                photoUrl: savedPlace.photoUrl || "",
                placeId: savedPlace.placeId || "",
                rating: typeof savedPlace.rating === "number" ? savedPlace.rating : null,
                ts: nowDate,
                typeLabel: savedPlace.typeLabel || "",
                updatedAt: nowDate,
                userRatings: typeof savedPlace.userRatings === "number" ? savedPlace.userRatings : 0,
                openNow: !!savedPlace.openNow,
            };
        },

        async saveRecommendedPlace(place) {
            const nowMs = Date.now();
            const nowDate = new Date(nowMs);
            const savedRef = doc(sentrysDb, "saved_places", place.id);

            await setDoc(savedRef, {
                id: place.id,
                placeId: place.placeId || null,   // ✅ това липсваше
                name: place.name || "",
                category: place.category || "",
                address: place.address || "",
                lat: place.lat ?? null,
                lng: place.lng ?? null,
                photoUrl: place.photoUrl || null,
                photoName: place.photoName || null,

                typeLabel: place.typeLabel || "",
                rating: typeof place.rating === "number" ? place.rating : null,
                userRatings: typeof place.userRatings === "number" ? place.userRatings : 0,

                isSponsored: !!place.isSponsored,
                openNow: place.openNow ?? null,

                // полезно за Saved Places таблицата
                createdAt: place.createdAt || nowDate,
                savedAt: place.savedAt || nowDate,
                updatedAt: nowDate
            }, { merge: true });
        },

        async updateSponsoredState(place) {
            try {
                const recommendedRef = doc(sentrysDb, "recommended_places", place.id);

                await updateDoc(recommendedRef, {
                    isSponsored: !!place.isSponsored
                });
            } catch (error) {
                console.error("Update sponsored state error:", error);
                alert("Грешка при обновяване на sponsored state.");
            }
        },

        async deletePlace(place) {
            if (!confirm("Delete?")) return;

            await deleteDoc(doc(sentrysDb, "recommended_places", place.id));
        },

        async saveAllOrders() {
            const batch = writeBatch(sentrysDb);

            this.places.forEach((p, i) => {
                batch.update(doc(sentrysDb, "recommended_places", p.id), {
                    order: i
                });
            });

            await batch.commit();
        },

        onDragStart(index) {
            this.dragIndex = index;
        },

        async onDrop(index) {
            if (this.dragIndex === null) return;

            const item = this.places[this.dragIndex];
            this.places.splice(this.dragIndex, 1);
            this.places.splice(index, 0, item);

            this.places = this.places.map((p, i) => ({ ...p, order: i }));
            this.dragIndex = null;

            await this.saveAllOrders();
        },

        onManualPhotoSelected(event) {
            const file = event?.target?.files?.[0];
            if (!file) return;

            this.manualPhotoFile = file;

            if (this.manualPhotoPreview) {
                URL.revokeObjectURL(this.manualPhotoPreview);
            }

            this.manualPhotoPreview = URL.createObjectURL(file);
        },

        openPhotoUploadModal() {
            if (this.photoUploadModalInstance) {
                this.photoUploadModalInstance.show();
            }
        },

        closePhotoUploadModal() {
            this.resetManualPhotoState();

            if (this.photoUploadModalInstance) {
                this.photoUploadModalInstance.hide();
            }
        },

        async uploadPhotoToStorage() {
            const placeId = this.newPlace.placeId;
            const photoName = `recommended_photos/${placeId}.jpg`;
            const storage = getStorage();
            const storageRef = ref(storage, photoName);

            let sourceBlob = null;

            if (this.manualPhotoFile) {
                sourceBlob = this.manualPhotoFile;
            } else if (this.newPlace.photoUrl) {
                sourceBlob = await this.fetchBlobFromUrl(this.newPlace.photoUrl);
            } else {
                throw new Error("No photo source available.");
            }

            await uploadBytes(storageRef, sourceBlob, {
                contentType: sourceBlob.type || "image/jpeg",
            });

            const photoUrl = await getDownloadURL(storageRef);

            return {
                photoName,
                photoUrl,
            };
        },

        async fetchBlobFromUrl(url) {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error("Failed to fetch Google photo blob.");
            }

            return await response.blob();
        },

        resetNewPlace() {
            this.newPlace = {
                name: "",
                address: "",
                lat: null,
                lng: null,
                placeId: "",
                photoUrl: "",
                category: "",
                isSponsored: false,
                openNow: false,
                rating: null,
                userRatings: 0,
                typeLabel: "",
                photoName: "",
            };
        },

        resetManualPhotoState() {
            this.manualPhotoFile = null;

            if (this.manualPhotoPreview) {
                URL.revokeObjectURL(this.manualPhotoPreview);
            }

            this.manualPhotoPreview = "";
        },

        resetAfterPlaceAction() {
            this.searchQuery = "";
            this.suggestions = [];
            this.selectedSuggestion = null;
            this.selectedSuggestionLabel = "";
            this.pendingAction = null;
            this.resetNewPlace();
            this.closePhotoUploadModal();

            if (this.AutocompleteSessionTokenClass) {
                this.sessionToken = new this.AutocompleteSessionTokenClass();
            }
        },

        toMillis(value) {
            if (!value) return 0;

            if (typeof value === "number") return value;

            if (value.toDate && typeof value.toDate === "function") {
                return value.toDate().getTime();
            }

            const parsed = new Date(value).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
        },

        onImageError(e) {
            e.target.src = "https://via.placeholder.com/60x60?text=No+Img";
        },

        formatTimestamp(value) {
            if (!value) return "-";

            try {
                const date = value.toDate ? value.toDate() : new Date(value);

                return new Intl.DateTimeFormat("bg-BG", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                }).format(date);
            } catch {
                return "-";
            }
        },
    },
};
</script>

<style lang="scss">
@import './scss/styles.scss';
</style>